import { PALETTE } from '../StageData';
import { ShadingPipeline } from '../../engine/ShadingPipeline';
import type { ShapeDrawFn } from './furnitureShapes';

export const landmarkDrawFns: Record<number, ShapeDrawFn> = {
  // STAGE 101: 富士山と五重塔 (Mount Fuji & Pagoda)
  101: (ctx, cx, cy, mode) => {
    // 富士山背景
    const drawFuji = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 150, cy + 120);
      ctx.quadraticCurveTo(cx - 90, cy - 20, cx - 35, cy - 80);
      ctx.lineTo(cx + 35, cy - 80);
      ctx.quadraticCurveTo(cx + 90, cy - 20, cx + 150, cy + 120);
      ctx.closePath();
    };

    // 富士山の雪冠
    const drawSnowCap = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 35, cy - 80);
      ctx.lineTo(cx + 35, cy - 80);
      ctx.quadraticCurveTo(cx + 55, cy - 40, cx + 60, cy - 25);
      ctx.lineTo(cx + 40, cy - 35);
      ctx.lineTo(cx + 25, cy - 20);
      ctx.lineTo(cx + 10, cy - 35);
      ctx.lineTo(cx - 5, cy - 22);
      ctx.lineTo(cx - 20, cy - 35);
      ctx.lineTo(cx - 40, cy - 20);
      ctx.quadraticCurveTo(cx - 50, cy - 45, cx - 35, cy - 80);
      ctx.closePath();
    };

    // 五重塔（手前右寄り）
    const drawPagodaRoof = (levelY: number, width: number) => {
      ctx.beginPath();
      ctx.moveTo(cx - width / 2, levelY);
      ctx.quadraticCurveTo(cx, levelY - 12, cx + width / 2, levelY);
      ctx.lineTo(cx + width / 2 - 8, levelY + 12);
      ctx.quadraticCurveTo(cx, levelY + 6, cx - width / 2 + 8, levelY + 12);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5';
      drawFuji(); ctx.fill();
      ctx.fillStyle = '#FAF8F5';
      drawSnowCap(); ctx.fill();

      // 五重塔ベース
      ctx.fillStyle = '#FAF8F5';
      ctx.fillRect(cx - 25, cy - 50, 50, 160);
      for (let i = 0; i < 5; i++) {
        drawPagodaRoof(cy - 50 + i * 28, 90 - i * 8);
        ctx.fill();
      }
      // 相輪（頂上）
      ctx.fillRect(cx - 3, cy - 95, 6, 45);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawFuji(); ctx.stroke();
        drawSnowCap(); ctx.stroke();

        // 塔の相輪
        ctx.strokeRect(cx - 3, cy - 95, 6, 45);
        ctx.beginPath(); ctx.arc(cx, cy - 95, 6, 0, Math.PI * 2); ctx.stroke();

        // 五重の屋根と層
        for (let i = 0; i < 5; i++) {
          const y = cy - 50 + i * 28;
          const w = 90 - i * 8;
          drawPagodaRoof(y, w); ctx.stroke();
          ctx.strokeRect(cx - 20, y + 12, 40, 16);
          // 欄干
          ctx.beginPath();
          ctx.moveTo(cx - 22, y + 26);
          ctx.lineTo(cx + 22, y + 26);
          ctx.stroke();
        }
      });
    } else if (mode === 'targetPreview') {
      // 富士山の群青
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 20, 300, 200, PALETTE.deepBlue, 'matte');
      drawFuji(); ctx.fill();

      // 雪化粧
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 50, 120, 80, PALETTE.white, 'ceramic');
      drawSnowCap(); ctx.fill();

      // 五重塔の漆朱塗りと瓦
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 30, 60, 160, PALETTE.crimson, 'matte');
      ctx.fillRect(cx - 25, cy - 50, 50, 160);

      for (let i = 0; i < 5; i++) {
        const y = cy - 50 + i * 28;
        const w = 90 - i * 8;
        ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, y, w, 20, PALETTE.charcoal, 'matte');
        drawPagodaRoof(y, w); ctx.fill();
      }

      // 相輪（黄金）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 75, 20, 50, PALETTE.gold, 'metallic');
      ctx.fillRect(cx - 3, cy - 95, 6, 45);
      ctx.beginPath(); ctx.arc(cx, cy - 95, 6, 0, Math.PI * 2); ctx.fill();

      ShadingPipeline.renderSpecularGlint(ctx, cx + 15, cy - 65, 12, 10, 0.6);
    }
  },

  // STAGE 102: 浅草寺 雷門 (Kaminarimon Gate)
  102: (ctx, cx, cy, mode) => {
    // 屋根
    const drawRoof = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 130, cy - 50);
      ctx.quadraticCurveTo(cx, cy - 85, cx + 130, cy - 50);
      ctx.lineTo(cx + 120, cy - 25);
      ctx.quadraticCurveTo(cx, cy - 50, cx - 120, cy - 25);
      ctx.closePath();
    };

    // 大提灯
    const drawLantern = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy + 35, 52, 65, 0, 0, Math.PI * 2);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawRoof(); ctx.fill();
      // 柱
      ctx.fillStyle = '#FAF8F5';
      ctx.fillRect(cx - 95, cy - 25, 25, 135);
      ctx.fillRect(cx + 70, cy - 25, 25, 135);
      ctx.fillRect(cx - 95, cy - 15, 190, 20);
      // 提灯
      ctx.fillStyle = '#FAF8F5';
      drawLantern(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawRoof(); ctx.stroke();
        // 梁と柱
        ctx.strokeRect(cx - 95, cy - 25, 25, 135);
        ctx.strokeRect(cx + 70, cy - 25, 25, 135);
        ctx.strokeRect(cx - 95, cy - 15, 190, 20);
        // 提灯輪郭と輪
        drawLantern(); ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(cx, cy - 25, 30, 8, 0, 0, Math.PI * 2);
        ctx.ellipse(cx, cy + 95, 30, 8, 0, 0, Math.PI * 2);
        ctx.stroke();
        // 提灯の骨組み縦線
        for (let x = -35; x <= 35; x += 17) {
          ctx.beginPath();
          ctx.moveTo(cx + x, cy - 20);
          ctx.quadraticCurveTo(cx + x * 1.3, cy + 35, cx + x, cy + 90);
          ctx.stroke();
        }
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 55, 260, 60, PALETTE.charcoal, 'matte');
      drawRoof(); ctx.fill();

      // 柱（丹塗り）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx - 82, cy + 40, 25, 135, PALETTE.crimson, 'wood');
      ctx.fillRect(cx - 95, cy - 25, 25, 135);
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx + 82, cy + 40, 25, 135, PALETTE.crimson, 'wood');
      ctx.fillRect(cx + 70, cy - 25, 25, 135);
      ctx.fillRect(cx - 95, cy - 15, 190, 20);

      // 大提灯（深紅ウレタン）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 35, 104, 130, PALETTE.red, 'ceramic');
      drawLantern(); ctx.fill();

      // 提灯の上下金具（漆黒）
      ctx.fillStyle = PALETTE.black;
      ctx.beginPath(); ctx.ellipse(cx, cy - 25, 32, 9, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(cx, cy + 95, 32, 9, 0, 0, Math.PI * 2); ctx.fill();

      ShadingPipeline.renderSpecularGlint(ctx, cx - 20, cy + 15, 20, 35, 0.5);
    }
  },

  // STAGE 103: 姫路城・白鷺城 (Himeji Castle)
  103: (ctx, cx, cy, mode) => {
    const drawStoneWall = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 120, cy + 110);
      ctx.lineTo(cx - 85, cy + 45);
      ctx.lineTo(cx + 85, cy + 45);
      ctx.lineTo(cx + 120, cy + 110);
      ctx.closePath();
    };

    const drawCastleRoof = (y: number, w: number, h: number) => {
      ctx.beginPath();
      ctx.moveTo(cx - w / 2, y);
      ctx.quadraticCurveTo(cx, y - h, cx + w / 2, y);
      ctx.lineTo(cx + w / 2 - 10, y + 14);
      ctx.quadraticCurveTo(cx, y - h + 10, cx - w / 2 + 10, y + 14);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawStoneWall(); ctx.fill();
      ctx.fillStyle = '#FAF8F5';
      ctx.fillRect(cx - 65, cy - 60, 130, 105);
      // 各重屋根
      ctx.fillStyle = '#FAF8F5';
      drawCastleRoof(cy + 35, 150, 16); ctx.fill();
      drawCastleRoof(cy, 120, 16); ctx.fill();
      drawCastleRoof(cy - 35, 90, 18); ctx.fill();
      drawCastleRoof(cy - 70, 60, 18); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawStoneWall(); ctx.stroke();
        // 石垣の目地
        for (let y = cy + 60; y < cy + 110; y += 15) {
          ctx.beginPath(); ctx.moveTo(cx - 100, y); ctx.lineTo(cx + 100, y); ctx.stroke();
        }
        // 白漆喰壁と小窓
        ctx.strokeRect(cx - 65, cy - 60, 130, 105);
        for (let y = cy - 45; y <= cy + 15; y += 30) {
          ctx.strokeRect(cx - 45, y, 16, 12);
          ctx.strokeRect(cx - 10, y, 20, 12);
          ctx.strokeRect(cx + 30, y, 16, 12);
        }
        // 屋根と破風
        drawCastleRoof(cy + 35, 150, 16); ctx.stroke();
        drawCastleRoof(cy, 120, 16); ctx.stroke();
        drawCastleRoof(cy - 35, 90, 18); ctx.stroke();
        drawCastleRoof(cy - 70, 60, 18); ctx.stroke();
        // 鯱（左右）
        ctx.beginPath(); ctx.arc(cx - 28, cy - 72, 5, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx + 28, cy - 72, 5, 0, Math.PI * 2); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      // 石垣グレー
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 75, 240, 65, PALETTE.silver, 'matte');
      drawStoneWall(); ctx.fill();

      // 白漆喰の白
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 10, 130, 105, PALETTE.white, 'ceramic');
      ctx.fillRect(cx - 65, cy - 60, 130, 105);

      // 黒漆窓
      ctx.fillStyle = PALETTE.dark;
      for (let y = cy - 45; y <= cy + 15; y += 30) {
        ctx.fillRect(cx - 45, y, 16, 12);
        ctx.fillRect(cx - 10, y, 20, 12);
        ctx.fillRect(cx + 30, y, 16, 12);
      }

      // 瓦屋根（藍墨瓦）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 150, 100, PALETTE.charcoal, 'matte');
      drawCastleRoof(cy + 35, 150, 16); ctx.fill();
      drawCastleRoof(cy, 120, 16); ctx.fill();
      drawCastleRoof(cy - 35, 90, 18); ctx.fill();
      drawCastleRoof(cy - 70, 60, 18); ctx.fill();

      // 金鯱（山吹ゴールド）
      ctx.fillStyle = PALETTE.gold;
      ctx.beginPath(); ctx.arc(cx - 28, cy - 72, 5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx + 28, cy - 72, 5, 0, Math.PI * 2); ctx.fill();

      ShadingPipeline.renderSpecularGlint(ctx, cx - 20, cy - 20, 20, 30, 0.7);
    }
  },

  // STAGE 104: 東京タワー (Tokyo Tower)
  104: (ctx, cx, cy, mode) => {
    const drawLegs = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 100, cy + 115);
      ctx.quadraticCurveTo(cx - 50, cy + 30, cx - 25, cy - 20);
      ctx.lineTo(cx + 25, cy - 20);
      ctx.quadraticCurveTo(cx + 50, cy + 30, cx + 100, cy + 115);
      ctx.lineTo(cx + 65, cy + 115);
      ctx.quadraticCurveTo(cx, cy + 60, cx - 65, cy + 115);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawLegs(); ctx.fill();
      // 上部尖塔
      ctx.beginPath();
      ctx.moveTo(cx - 20, cy - 20);
      ctx.lineTo(cx - 5, cy - 100);
      ctx.lineTo(cx + 5, cy - 100);
      ctx.lineTo(cx + 20, cy - 20);
      ctx.fill();
      // 展望台
      ctx.fillStyle = '#FAF8F5';
      ctx.fillRect(cx - 32, cy - 20, 64, 25);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawLegs(); ctx.stroke();
        // 展望台
        ctx.strokeRect(cx - 32, cy - 20, 64, 25);
        ctx.strokeRect(cx - 20, cy - 55, 40, 15);
        // 上部尖塔とアンテナ
        ctx.beginPath();
        ctx.moveTo(cx - 20, cy - 20);
        ctx.lineTo(cx - 4, cy - 95);
        ctx.lineTo(cx + 4, cy - 95);
        ctx.lineTo(cx + 20, cy - 20);
        ctx.stroke();
        ctx.strokeRect(cx - 1.5, cy - 120, 3, 25);
        // トラス構造のクロス線
        for (let y = cy + 20; y < cy + 100; y += 22) {
          ctx.beginPath();
          ctx.moveTo(cx - 45, y); ctx.lineTo(cx + 45, y + 15);
          ctx.moveTo(cx + 45, y); ctx.lineTo(cx - 45, y + 15);
          ctx.stroke();
        }
      });
    } else if (mode === 'targetPreview') {
      // インターナショナルオレンジ
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 50, 200, 130, PALETTE.orange, 'metallic');
      drawLegs(); ctx.fill();

      // 上部トラス（オレンジと白の縞）
      ctx.beginPath();
      ctx.moveTo(cx - 20, cy - 20);
      ctx.lineTo(cx - 4, cy - 95);
      ctx.lineTo(cx + 4, cy - 95);
      ctx.lineTo(cx + 20, cy - 20);
      ctx.fill();

      // メイン展望台（ホワイト＆ガラス）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 8, 64, 25, PALETTE.white, 'ceramic');
      ctx.fillRect(cx - 32, cy - 20, 64, 25);
      ctx.fillStyle = PALETTE.navy;
      ctx.fillRect(cx - 28, cy - 15, 56, 8);

      // 特別展望台
      ctx.fillStyle = PALETTE.white;
      ctx.fillRect(cx - 20, cy - 55, 40, 15);

      // アンテナ（シルバー）
      ctx.fillStyle = PALETTE.silver;
      ctx.fillRect(cx - 1.5, cy - 120, 3, 25);

      ShadingPipeline.renderSpecularGlint(ctx, cx - 10, cy + 30, 25, 40, 0.6);
    }
  },

  // STAGE 105: 厳島神社 水上大鳥居 (Miyajima Floating Torii)
  105: (ctx, cx, cy, mode) => {
    // 笠木・島木（上部反り屋根）
    const drawKasagi = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 125, cy - 65);
      ctx.quadraticCurveTo(cx, cy - 80, cx + 125, cy - 65);
      ctx.lineTo(cx + 120, cy - 45);
      ctx.quadraticCurveTo(cx, cy - 60, cx - 120, cy - 45);
      ctx.closePath();
    };

    // 貫（水平梁）
    const drawNuki = () => {
      ctx.fillRect(cx - 105, cy - 25, 210, 16);
    };

    // 水面の波
    const drawWaves = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 140, cy + 95);
      for (let x = cx - 140; x <= cx + 140; x += 35) {
        ctx.quadraticCurveTo(x + 8, cy + 88, x + 17.5, cy + 95);
        ctx.quadraticCurveTo(x + 26, cy + 102, x + 35, cy + 95);
      }
      ctx.lineTo(cx + 140, cy + 120);
      ctx.lineTo(cx - 140, cy + 120);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawWaves(); ctx.fill();
      ctx.fillStyle = '#FAF8F5';
      drawKasagi(); ctx.fill();
      drawNuki();
      // 主柱2本
      ctx.fillRect(cx - 65, cy - 45, 22, 140);
      ctx.fillRect(cx + 43, cy - 45, 22, 140);
      // 袖柱（支え柱）
      ctx.fillRect(cx - 90, cy + 15, 14, 80);
      ctx.fillRect(cx + 76, cy + 15, 14, 80);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawKasagi(); ctx.stroke();
        ctx.strokeRect(cx - 105, cy - 25, 210, 16);
        // 主柱
        ctx.strokeRect(cx - 65, cy - 45, 22, 140);
        ctx.strokeRect(cx + 43, cy - 45, 22, 140);
        // 袖柱
        ctx.strokeRect(cx - 90, cy + 15, 14, 80);
        ctx.strokeRect(cx + 76, cy + 15, 14, 80);
        // 額束（中央の看板）
        ctx.strokeRect(cx - 12, cy - 45, 24, 20);
        // 波紋
        drawWaves(); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      // 瀬戸内海の群青波
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 105, 280, 40, PALETTE.blue, 'matte');
      drawWaves(); ctx.fill();

      // 鳥居（極上の朱塗り丹色）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 55, 250, 40, PALETTE.crimson, 'wood');
      drawKasagi(); ctx.fill();
      drawNuki();

      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx - 54, cy + 25, 22, 140, PALETTE.crimson, 'wood');
      ctx.fillRect(cx - 65, cy - 45, 22, 140);
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx + 54, cy + 25, 22, 140, PALETTE.crimson, 'wood');
      ctx.fillRect(cx + 43, cy - 45, 22, 140);

      // 袖柱
      ctx.fillRect(cx - 90, cy + 15, 14, 80);
      ctx.fillRect(cx + 76, cy + 15, 14, 80);

      // 額束の黄金
      ctx.fillStyle = PALETTE.gold;
      ctx.fillRect(cx - 12, cy - 45, 24, 20);

      ShadingPipeline.renderSpecularGlint(ctx, cx - 50, cy - 10, 15, 45, 0.6);
    }
  },

  // STAGE 106: 金閣寺・鹿苑寺 (Kinkaku-ji Golden Pavilion)
  106: (ctx, cx, cy, mode) => {
    const drawPavilionRoof = (y: number, w: number, curve: number) => {
      ctx.beginPath();
      ctx.moveTo(cx - w / 2, y);
      ctx.quadraticCurveTo(cx, y - curve, cx + w / 2, y);
      ctx.lineTo(cx + w / 2 - 8, y + 14);
      ctx.quadraticCurveTo(cx, y - curve + 8, cx - w / 2 + 8, y + 14);
      ctx.closePath();
    };

    const drawWater = () => {
      ctx.beginPath();
      ctx.rect(cx - 130, cy + 70, 260, 45);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawWater(); ctx.fill();
      ctx.fillStyle = '#FAF8F5';
      // 3層構造
      ctx.fillRect(cx - 60, cy + 15, 120, 55); // 1階（法水院）
      ctx.fillRect(cx - 50, cy - 25, 100, 40); // 2階（潮音洞）
      ctx.fillRect(cx - 35, cy - 65, 70, 40);  // 3階（究竟頂）
      // 屋根
      drawPavilionRoof(cy + 15, 145, 18); ctx.fill();
      drawPavilionRoof(cy - 25, 120, 18); ctx.fill();
      drawPavilionRoof(cy - 65, 90, 22); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        // 3層の柱・格子窓
        ctx.strokeRect(cx - 60, cy + 15, 120, 55);
        ctx.strokeRect(cx - 50, cy - 25, 100, 40);
        ctx.strokeRect(cx - 35, cy - 65, 70, 40);

        // 3重の屋根
        drawPavilionRoof(cy + 15, 145, 18); ctx.stroke();
        drawPavilionRoof(cy - 25, 120, 18); ctx.stroke();
        drawPavilionRoof(cy - 65, 90, 22); ctx.stroke();

        // 頂上の鳳凰
        ctx.beginPath();
        ctx.moveTo(cx, cy - 85); ctx.lineTo(cx, cy - 70);
        ctx.arc(cx, cy - 85, 5, 0, Math.PI * 2);
        ctx.stroke();

        // 鏡湖池と水草
        drawWater(); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      // 鏡湖池（エメラルド水面）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 90, 260, 45, PALETTE.turquoise, 'matte');
      drawWater(); ctx.fill();

      // 1階（白木造り）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 42, 120, 55, PALETTE.wood, 'wood');
      ctx.fillRect(cx - 60, cy + 15, 120, 55);

      // 2階・3階（極上の純金箔）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 45, 100, 80, PALETTE.gold, 'metallic');
      ctx.fillRect(cx - 50, cy - 25, 100, 40);
      ctx.fillRect(cx - 35, cy - 65, 70, 40);

      // こけら葺き屋根（深みのある檜皮茶色）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 30, 145, 100, PALETTE.darkWood, 'matte');
      drawPavilionRoof(cy + 15, 145, 18); ctx.fill();
      drawPavilionRoof(cy - 25, 120, 18); ctx.fill();
      drawPavilionRoof(cy - 65, 90, 22); ctx.fill();

      // 黄金の鳳凰
      ctx.fillStyle = PALETTE.gold;
      ctx.beginPath(); ctx.arc(cx, cy - 85, 5, 0, Math.PI * 2); ctx.fill();

      ShadingPipeline.renderSpecularGlint(ctx, cx - 15, cy - 45, 25, 20, 0.85);
    }
  },

  // STAGE 107: エッフェル塔 (Eiffel Tower - Paris)
  107: (ctx, cx, cy, mode) => {
    const drawBaseArch = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 95, cy + 115);
      ctx.quadraticCurveTo(cx - 60, cy + 20, cx - 35, cy + 10);
      ctx.lineTo(cx + 35, cy + 10);
      ctx.quadraticCurveTo(cx + 60, cy + 20, cx + 95, cy + 115);
      ctx.lineTo(cx + 60, cy + 115);
      ctx.quadraticCurveTo(cx, cy + 45, cx - 60, cy + 115);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawBaseArch(); ctx.fill();
      // 上部塔身
      ctx.beginPath();
      ctx.moveTo(cx - 30, cy + 10);
      ctx.lineTo(cx - 4, cy - 100);
      ctx.lineTo(cx + 4, cy - 100);
      ctx.lineTo(cx + 30, cy + 10);
      ctx.fill();
      // 展望デッキ
      ctx.fillRect(cx - 45, cy + 10, 90, 15);
      ctx.fillRect(cx - 25, cy - 40, 50, 12);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawBaseArch(); ctx.stroke();
        // 展望台
        ctx.strokeRect(cx - 45, cy + 10, 90, 15);
        ctx.strokeRect(cx - 25, cy - 40, 50, 12);
        // 上部尖塔とドーム
        ctx.beginPath();
        ctx.moveTo(cx - 25, cy - 40);
        ctx.lineTo(cx - 4, cy - 100);
        ctx.lineTo(cx + 4, cy - 100);
        ctx.lineTo(cx + 25, cy - 40);
        ctx.stroke();
        ctx.beginPath(); ctx.arc(cx, cy - 102, 4, 0, Math.PI * 2); ctx.stroke();
        ctx.strokeRect(cx - 1, cy - 120, 2, 18);
        // パリの錬鉄トラスクロス
        for (let y = cy - 30; y < cy + 100; y += 20) {
          ctx.beginPath();
          ctx.moveTo(cx - 20, y); ctx.lineTo(cx + 20, y + 12);
          ctx.moveTo(cx + 20, y); ctx.lineTo(cx - 20, y + 12);
          ctx.stroke();
        }
      });
    } else if (mode === 'targetPreview') {
      // パリジャンブロンズ
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 40, 190, 150, PALETTE.brass, 'metallic');
      drawBaseArch(); ctx.fill();

      // 上部塔身
      ctx.beginPath();
      ctx.moveTo(cx - 30, cy + 10);
      ctx.lineTo(cx - 4, cy - 100);
      ctx.lineTo(cx + 4, cy - 100);
      ctx.lineTo(cx + 30, cy + 10);
      ctx.fill();

      // デッキ（ゴールドとブロンズのアクセント）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 17, 90, 15, PALETTE.gold, 'metallic');
      ctx.fillRect(cx - 45, cy + 10, 90, 15);
      ctx.fillRect(cx - 25, cy - 40, 50, 12);

      // ライトアップ・アンテナ
      ctx.fillStyle = PALETTE.silver;
      ctx.fillRect(cx - 1, cy - 120, 2, 18);

      ShadingPipeline.renderSpecularGlint(ctx, cx - 12, cy - 10, 25, 50, 0.7);
    }
  },

  // STAGE 108: ギザの大ピラミッドとスフィンクス (Pyramids of Giza & Sphinx)
  108: (ctx, cx, cy, mode) => {
    const drawMainPyramid = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 130, cy + 60);
      ctx.lineTo(cx - 20, cy - 70);
      ctx.lineTo(cx + 60, cy + 60);
      ctx.closePath();
    };

    const drawSidePyramid = () => {
      ctx.beginPath();
      ctx.moveTo(cx + 20, cy + 60);
      ctx.lineTo(cx + 90, cy - 30);
      ctx.lineTo(cx + 140, cy + 60);
      ctx.closePath();
    };

    const drawSphinx = () => {
      // スフィンクス頭部と前脚
      ctx.beginPath();
      ctx.ellipse(cx - 50, cy + 75, 22, 18, 0, 0, Math.PI * 2);
      ctx.rect(cx - 70, cy + 85, 75, 25);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5';
      drawMainPyramid(); ctx.fill();
      drawSidePyramid(); ctx.fill();
      ctx.fillStyle = '#FAF8F5';
      drawSphinx(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawMainPyramid(); ctx.stroke();
        // ピラミッドの稜線
        ctx.beginPath(); ctx.moveTo(cx - 20, cy - 70); ctx.lineTo(cx - 30, cy + 60); ctx.stroke();
        drawSidePyramid(); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx + 90, cy - 30); ctx.lineTo(cx + 80, cy + 60); ctx.stroke();
        // 石段の水平テクスチャ
        for (let y = cy - 40; y < cy + 60; y += 18) {
          ctx.beginPath(); ctx.moveTo(cx - 90 + (y - cy) * 0.7, y); ctx.lineTo(cx + 30 - (y - cy) * 0.5, y); ctx.stroke();
        }
        // スフィンクス
        drawSphinx(); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx - 50, cy + 70, 12, 0, Math.PI * 2); ctx.stroke(); // ネメス頭巾
      });
    } else if (mode === 'targetPreview') {
      // 大ピラミッド（砂漠のゴールデンサンド）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx - 35, cy, 190, 130, PALETTE.gold, 'matte');
      drawMainPyramid(); ctx.fill();

      // 影側のトーン（アンバー）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx + 15, cy, 90, 130, PALETTE.orange, 'matte');
      ctx.beginPath();
      ctx.moveTo(cx - 20, cy - 70);
      ctx.lineTo(cx - 30, cy + 60);
      ctx.lineTo(cx + 60, cy + 60);
      ctx.closePath();
      ctx.fill();

      // 副ピラミッド
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx + 80, cy + 15, 120, 90, PALETTE.yellow, 'matte');
      drawSidePyramid(); ctx.fill();

      // スフィンクス（石灰岩ベージュ）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx - 40, cy + 85, 80, 35, PALETTE.amber, 'matte');
      drawSphinx(); ctx.fill();

      ShadingPipeline.renderSpecularGlint(ctx, cx - 50, cy - 20, 30, 25, 0.6);
    }
  },

  // STAGE 109: 自由の女神像 (Statue of Liberty - New York)
  109: (ctx, cx, cy, mode) => {
    const drawPedestal = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 50, cy + 115);
      ctx.lineTo(cx - 40, cy + 50);
      ctx.lineTo(cx + 40, cy + 50);
      ctx.lineTo(cx + 50, cy + 115);
      ctx.closePath();
    };

    const drawStatue = () => {
      // 衣とローブ
      ctx.beginPath();
      ctx.moveTo(cx - 25, cy + 50);
      ctx.lineTo(cx - 20, cy - 35);
      ctx.lineTo(cx + 20, cy - 35);
      ctx.lineTo(cx + 28, cy + 50);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawPedestal(); ctx.fill();
      ctx.fillStyle = '#FAF8F5'; drawStatue(); ctx.fill();
      // 右腕とトーチ
      ctx.fillRect(cx + 15, cy - 75, 10, 45);
      ctx.beginPath(); ctx.arc(cx + 20, cy - 85, 12, 0, Math.PI * 2); ctx.fill();
      // 頭部
      ctx.beginPath(); ctx.arc(cx, cy - 45, 15, 0, Math.PI * 2); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawPedestal(); ctx.stroke();
        ctx.strokeRect(cx - 42, cy + 50, 84, 18);
        drawStatue(); ctx.stroke();
        // ローブのドレープ線
        for (let x = cx - 12; x <= cx + 15; x += 9) {
          ctx.beginPath();
          ctx.moveTo(x, cy - 30);
          ctx.quadraticCurveTo(x + 3, cy + 10, x - 2, cy + 50);
          ctx.stroke();
        }
        // 頭部と7つの光冠（王冠）
        ctx.beginPath(); ctx.arc(cx, cy - 45, 14, 0, Math.PI * 2); ctx.stroke();
        for (let a = -Math.PI * 0.8; a <= -Math.PI * 0.2; a += Math.PI * 0.1) {
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a) * 14, cy - 45 + Math.sin(a) * 14);
          ctx.lineTo(cx + Math.cos(a) * 26, cy - 45 + Math.sin(a) * 26);
          ctx.stroke();
        }
        // 右腕・トーチの炎
        ctx.beginPath();
        ctx.moveTo(cx + 15, cy - 30);
        ctx.lineTo(cx + 25, cy - 75);
        ctx.stroke();
        ctx.beginPath(); ctx.arc(cx + 25, cy - 85, 10, 0, Math.PI * 2); ctx.stroke();
        // 独立宣言書（左手）
        ctx.strokeRect(cx - 28, cy - 10, 14, 20);
      });
    } else if (mode === 'targetPreview') {
      // 石造り台座（グランストーン）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 85, 100, 65, PALETTE.silver, 'matte');
      drawPedestal(); ctx.fill();

      // 女神像（ヴェルディグリ緑青パティナ）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 10, 60, 90, PALETTE.cyan, 'metallic');
      drawStatue(); ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy - 45, 14, 0, Math.PI * 2); ctx.fill();

      // 独立宣言書
      ctx.fillStyle = PALETTE.ivory;
      ctx.fillRect(cx - 28, cy - 10, 14, 20);

      // 松明の炎（黄金の輝き）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx + 25, cy - 85, 20, 20, PALETTE.gold, 'metallic');
      ctx.beginPath(); ctx.arc(cx + 25, cy - 85, 10, 0, Math.PI * 2); ctx.fill();

      ShadingPipeline.renderSpecularGlint(ctx, cx - 8, cy - 15, 18, 30, 0.7);
    }
  },

  // STAGE 110: タージ・マハル (Taj Mahal - India)
  110: (ctx, cx, cy, mode) => {
    // 中央の玉ねぎ型ドーム
    const drawMainDome = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 45, cy - 30);
      ctx.quadraticCurveTo(cx - 50, cy - 65, cx, cy - 90);
      ctx.quadraticCurveTo(cx + 50, cy - 65, cx + 45, cy - 30);
      ctx.closePath();
    };

    // 霊廟本体
    const drawMausoleum = () => {
      ctx.beginPath();
      ctx.rect(cx - 75, cy - 30, 150, 110);
    };

    // 4本の尖塔（ミナレット）
    const drawMinarets = () => {
      ctx.fillRect(cx - 110, cy - 60, 12, 140);
      ctx.fillRect(cx + 98, cy - 60, 12, 140);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5';
      drawMausoleum(); ctx.fill();
      drawMainDome(); ctx.fill();
      drawMinarets();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawMausoleum(); ctx.stroke();
        drawMainDome(); ctx.stroke();
        drawMinarets(); ctx.stroke();
        // ミナレットの頂上ドーム
        ctx.beginPath(); ctx.arc(cx - 104, cy - 65, 8, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx + 104, cy - 65, 8, 0, Math.PI * 2); ctx.stroke();
        // 中央の大アーチ門（アイヴァン）
        ctx.beginPath();
        ctx.moveTo(cx - 28, cy + 80);
        ctx.lineTo(cx - 28, cy + 15);
        ctx.quadraticCurveTo(cx, cy - 10, cx + 28, cy + 15);
        ctx.lineTo(cx + 28, cy + 80);
        ctx.stroke();
        // 左右の2段アーチ窓
        ctx.strokeRect(cx - 65, cy - 15, 20, 30);
        ctx.strokeRect(cx - 65, cy + 30, 20, 35);
        ctx.strokeRect(cx + 45, cy - 15, 20, 30);
        ctx.strokeRect(cx + 45, cy + 30, 20, 35);
      });
    } else if (mode === 'targetPreview') {
      // 純白大理石（アイボリーホワイト）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 25, 150, 110, PALETTE.white, 'ceramic');
      drawMausoleum(); ctx.fill();
      drawMainDome(); ctx.fill();

      // ミナレット
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 10, 230, 140, PALETTE.ivory, 'ceramic');
      drawMinarets();
      ctx.beginPath(); ctx.arc(cx - 104, cy - 65, 8, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx + 104, cy - 65, 8, 0, Math.PI * 2); ctx.fill();

      // 中央アーチの奥行き影
      ctx.fillStyle = PALETTE.navy;
      ctx.beginPath();
      ctx.moveTo(cx - 28, cy + 80);
      ctx.lineTo(cx - 28, cy + 15);
      ctx.quadraticCurveTo(cx, cy - 10, cx + 28, cy + 15);
      ctx.lineTo(cx + 28, cy + 80);
      ctx.fill();

      // 小窓
      ctx.fillStyle = PALETTE.charcoal;
      ctx.fillRect(cx - 65, cy - 15, 20, 30);
      ctx.fillRect(cx - 65, cy + 30, 20, 35);
      ctx.fillRect(cx + 45, cy - 15, 20, 30);
      ctx.fillRect(cx + 45, cy + 30, 20, 35);

      ShadingPipeline.renderSpecularGlint(ctx, cx - 15, cy - 50, 25, 30, 0.8);
    }
  },

  // STAGE 111: ピサの斜塔 (Leaning Tower of Pisa - Italy)
  111: (ctx, cx, cy, mode) => {
    // 傾斜角度（約4度 = 0.07ラジアン）
    const leanAngle = 0.07;

    const withLeanTransform = (drawFn: () => void) => {
      ctx.save();
      ctx.translate(cx, cy + 60);
      ctx.rotate(leanAngle);
      ctx.translate(-cx, -(cy + 60));
      drawFn();
      ctx.restore();
    };

    const drawTowerBody = () => {
      ctx.beginPath();
      ctx.rect(cx - 38, cy - 100, 76, 180);
    };

    if (mode === 'base') {
      withLeanTransform(() => {
        ctx.fillStyle = '#FAF8F5';
        drawTowerBody(); ctx.fill();
      });
      // 地面
      ctx.fillStyle = '#FAF8F5';
      ctx.fillRect(cx - 120, cy + 80, 240, 35);
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        withLeanTransform(() => {
          drawTowerBody(); ctx.stroke();
          // 8層のアーチ回廊
          for (let i = 0; i < 7; i++) {
            const y = cy - 80 + i * 22;
            ctx.strokeRect(cx - 42, y, 84, 18);
            for (let col = -30; col <= 30; col += 12) {
              ctx.strokeRect(cx + col - 2, y, 4, 18);
            }
          }
          // 最上部鐘楼
          ctx.strokeRect(cx - 28, cy - 100, 56, 20);
        });
        // 地面の芝生
        ctx.strokeRect(cx - 120, cy + 80, 240, 35);
      });
    } else if (mode === 'targetPreview') {
      withLeanTransform(() => {
        // トスカーナ大理石
        ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 10, 76, 180, PALETTE.white, 'ceramic');
        drawTowerBody(); ctx.fill();

        // 各層のアーチ回廊の影とハイライト
        for (let i = 0; i < 7; i++) {
          const y = cy - 80 + i * 22;
          ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, y + 9, 84, 18, PALETTE.ivory, 'ceramic');
          ctx.fillRect(cx - 42, y, 84, 18);
        }

        ctx.fillStyle = PALETTE.white;
        ctx.fillRect(cx - 28, cy - 100, 56, 20);
      });

      // トスカーナの芝生（オリーブグリーン）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 95, 240, 35, PALETTE.green, 'matte');
      ctx.fillRect(cx - 120, cy + 80, 240, 35);

      ShadingPipeline.renderSpecularGlint(ctx, cx - 18, cy - 30, 20, 60, 0.65);
    }
  },

  // STAGE 112: サグラダ・ファミリア (Sagrada Familia - Barcelona)
  112: (ctx, cx, cy, mode) => {
    // 4本の尖塔
    const drawSpire = (xOffset: number, height: number, width: number) => {
      ctx.beginPath();
      ctx.moveTo(cx + xOffset - width / 2, cy + 70);
      ctx.lineTo(cx + xOffset - width / 4, cy - height);
      ctx.lineTo(cx + xOffset + width / 4, cy - height);
      ctx.lineTo(cx + xOffset + width / 2, cy + 70);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5';
      // 中央ファサード
      ctx.fillRect(cx - 75, cy - 10, 150, 95);
      // 4本の主要尖塔
      drawSpire(-45, 95, 30); ctx.fill();
      drawSpire(-15, 115, 32); ctx.fill();
      drawSpire(15, 115, 32); ctx.fill();
      drawSpire(45, 95, 30); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        // ファサード本体と3つの門（希望・慈愛・信仰）
        ctx.strokeRect(cx - 75, cy - 10, 150, 95);
        for (let x = -40; x <= 40; x += 40) {
          ctx.beginPath();
          ctx.moveTo(cx + x - 14, cy + 85);
          ctx.lineTo(cx + x - 14, cy + 30);
          ctx.quadraticCurveTo(cx + x, cy + 10, cx + x + 14, cy + 30);
          ctx.lineTo(cx + x + 14, cy + 85);
          ctx.stroke();
        }

        // 4本の尖塔
        drawSpire(-45, 95, 30); ctx.stroke();
        drawSpire(-15, 115, 32); ctx.stroke();
        drawSpire(15, 115, 32); ctx.stroke();
        drawSpire(45, 95, 30); ctx.stroke();

        // 尖塔のピナクル（十字架・モザイク球）
        for (const [xOff, h] of [[-45, 95], [-15, 115], [15, 115], [45, 95]]) {
          ctx.beginPath(); ctx.arc(cx + xOff, cy - h - 6, 6, 0, Math.PI * 2); ctx.stroke();
          ctx.strokeRect(cx + xOff - 1, cy - h - 18, 2, 12);
        }

        // ガウディの幾何学ルーバー窓
        for (let y = cy - 70; y < cy + 10; y += 20) {
          ctx.strokeRect(cx - 50, y, 10, 12);
          ctx.strokeRect(cx - 20, y, 10, 12);
          ctx.strokeRect(cx + 10, y, 10, 12);
          ctx.strokeRect(cx + 40, y, 10, 12);
        }
      });
    } else if (mode === 'targetPreview') {
      // モンジュイックのサンドストーン（有機的な砂岩色）
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 20, 150, 100, PALETTE.wood, 'matte');
      ctx.fillRect(cx - 75, cy - 10, 150, 95);

      // 尖塔
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 20, 120, 140, PALETTE.darkWood, 'matte');
      drawSpire(-45, 95, 30); ctx.fill();
      drawSpire(-15, 115, 32); ctx.fill();
      drawSpire(15, 115, 32); ctx.fill();
      drawSpire(45, 95, 30); ctx.fill();

      // 頂上ピナクル（ガウディのベネチアンガラス・ゴールドモザイク）
      ctx.fillStyle = PALETTE.gold;
      for (const [xOff, h] of [[-45, 95], [-15, 115], [15, 115], [45, 95]]) {
        ctx.beginPath(); ctx.arc(cx + xOff, cy - h - 6, 6, 0, Math.PI * 2); ctx.fill();
      }

      // ステンドグラスの輝き（ステンドグラス虹色・群青）
      ctx.fillStyle = PALETTE.deepBlue;
      for (let x = -40; x <= 40; x += 40) {
        ctx.beginPath();
        ctx.moveTo(cx + x - 14, cy + 85);
        ctx.lineTo(cx + x - 14, cy + 30);
        ctx.quadraticCurveTo(cx + x, cy + 10, cx + x + 14, cy + 30);
        ctx.lineTo(cx + x + 14, cy + 85);
        ctx.fill();
      }

      ShadingPipeline.renderSpecularGlint(ctx, cx - 10, cy - 60, 20, 45, 0.75);
    }
  }
};
